import { parseCloudRun } from './parseCloud'
import { createListMemo } from './back4app/listMemo'
import Parse from './initializeParse'
import { parseUserId } from '../utils/parseUserId'
import type { RoomBookingData } from '../../../types/RoomBooking'

/**
 * How long one answer is shared between callers. The dashboard's meetings list and the reminders
 * hook ask for the same read one after the other, not together (the hook waits for the company
 * access gate, the list for the user id), so sharing only in-flight reads still cost two calls.
 * Short on purpose: a booking change bumps `refreshTrigger`, which is part of the key, so a
 * change still reads fresh; only a booking made by someone else can be missing, for up to this long.
 */
export const MY_BOOKINGS_READ_TTL_MS = 30_000

/**
 * How old the last dashboard read may be for the office join to reuse it instead of calling
 * getRoomBookings again (SC-477). Longer than the TTL above because the join only needs to know
 * which bookings the player is part of, and the in-room refresh on every booking change still
 * goes to the network. The trade-off: a booking someone else made in the last few minutes may be
 * missing from the join list until the next refresh (a knock modal can show for that room).
 */
export const MY_BOOKINGS_JOIN_REUSE_MAX_AGE_MS = 5 * 60_000

// Positive TTL: the answer is kept for MY_BOOKINGS_READ_TTL_MS, a failure is dropped, and expired
// entries are pruned when the next read starts (listMemo), so changing triggers do not pile up.
const memo = createListMemo(MY_BOOKINGS_READ_TTL_MS)

/**
 * The last successful read, kept after its memo entry expires so the office join can reuse it.
 * One record only: the memo is per user and the dashboard is per company, so a newer read for
 * another user or company simply replaces it.
 */
interface LastRead {
  userId: string
  companyId: string
  /** When the read started: the answer is at least this fresh, never fresher. */
  startedAt: number
  /** The `bookingsRefreshTrigger` the caller read under; a later change makes the answer stale. */
  refreshTrigger: number
  bookings: RoomBookingData[]
}
let lastRead: LastRead | null = null

function forgetLastRead() {
  lastRead = null
}
// Same window event the list memo listens to, so a forced sign-out drops the record too.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('sessionExpired', forgetLastRead)
}

/**
 * The company's upcoming bookings the user created or is invited to (declined ones left out by
 * cloud), in one `getRoomBookings` call.
 *
 * The Dashboard's meetings list and the reminders hook both need exactly this and mount together,
 * so a landing used to make three calls (SC-477). Callers that ask within MY_BOOKINGS_READ_TTL_MS
 * of a successful read, or while one is in flight, share it. `refreshTrigger` is the store's
 * `bookingsRefreshTrigger`: a read that started before a booking change must not answer a caller
 * who asks because of that change, so only callers with the same trigger share a read.
 *
 * Shared through the list memo, which scopes by the signed-in user read at call time: an
 * answer is scoped by the caller's session, so one account must never share a read started by
 * another (a sign-out and sign-in inside one window), and with no user nothing is shared.
 */
export function readMyUpcomingBookings(companyId: string, refreshTrigger: number): Promise<RoomBookingData[]> {
  return memo.get(`${companyId}:${refreshTrigger}`, () => {
    const userId = parseUserId(Parse)
    const startedAt = Date.now()
    return (
      // No sign-out on an expired token: this is background work, like App's booking refresh.
      parseCloudRun<{ bookings?: RoomBookingData[] }>(
        'getRoomBookings',
        { companyId, creatorOrInvitee: true },
        { handleInvalidSession: false }
      ).then((res) => {
        const bookings = res?.bookings || []
        // Only a success is recorded, and never an older read over a newer one (two reads of
        // different triggers can settle in either order).
        const sameScope = lastRead?.userId === userId && lastRead?.companyId === companyId
        if (userId && (!sameScope || startedAt >= lastRead!.startedAt)) {
          lastRead = { userId, companyId, startedAt, refreshTrigger, bookings }
        }
        return bookings
      })
    )
  })
}

/**
 * The bookings of the last successful `readMyUpcomingBookings` when it is the signed-in user's,
 * for this company, started under this `refreshTrigger` and under `maxAgeMs` ago; otherwise null
 * and the caller reads from the network. Makes no request. Never serves one account's answer to
 * another: the user is read at call time, and with no user the record is dropped.
 */
export function recentUpcomingBookings(
  companyId: string,
  refreshTrigger: number,
  maxAgeMs: number = MY_BOOKINGS_JOIN_REUSE_MAX_AGE_MS
): RoomBookingData[] | null {
  const userId = parseUserId(Parse)
  if (!userId) {
    forgetLastRead()
    return null
  }
  const rec = lastRead
  if (!rec || rec.userId !== userId || rec.companyId !== companyId) return null
  if (rec.refreshTrigger !== refreshTrigger) return null
  const age = Date.now() - rec.startedAt
  if (age < 0 || age >= maxAgeMs) return null
  return rec.bookings
}

/**
 * Whether `userId` created the booking or is invited to it without having declined. The same rule
 * as the Dashboard's meetings list, and a check against the read above in case it ever returns
 * more than the user's own.
 */
export function isOrganiserOrActiveInvitee(booking: RoomBookingData, userId: string): boolean {
  if (booking.creatorId === userId) return true
  const myInvite = booking.invitees?.find((inv) => inv.userId === userId)
  return !!myInvite && myInvite.status !== 'declined'
}
